import React, { useEffect, useState } from "react";
import { api, fmtUSD, fmtNumber } from "../lib/api";
import KpiCard from "../components/KpiCard";
import SeverityBadge from "../components/SeverityBadge";
import ServicePill from "../components/ServicePill";
import OpportunityDetailModal from "../components/OpportunityDetailModal";
import {
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Building2,
  Sparkles,
  ChevronRight,
  CheckCircle2,
} from "lucide-react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";

const SERVICE_COLORS = {
  BigQuery: "#00579e",
  "Cloud SQL": "#15803d",
  "Compute Engine": "#9a3412",
  "Vertex AI": "#6d28d9",
  GKE: "#0e7490",
  "Cloud Storage": "#374151",
  "Cloud Run": "#be185d",
  IAM: "#a16207",
  "Cloud Operations Suite": "#475569",
  "Cloud Pub/Sub": "#0d9488",
  "Amazon EC2": "#ff9900",
  "Amazon RDS": "#b45309",
  "Amazon S3": "#a16207",
  "Amazon Redshift": "#9a3412",
  "Virtual Machines": "#0078d4",
  "Azure SQL": "#075985",
  "Blob Storage": "#1d4ed8",
  AKS: "#0369a1",
  "Azure OpenAI": "#4338ca",
  "OCI Compute": "#c74634",
  "Autonomous Database": "#b91c1c",
  "Object Storage": "#9f1239",
  OKE: "#be123c",
};
const SEVERITY_COLORS = {
  critical: "#ef4444",
  high: "#f97316",
  medium: "#f59e0b",
  low: "#3b82f6",
};

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [costExpense, setCostExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const [s, ce] = await Promise.all([
        api.summary(),
        api.ce.costExpense(),
      ]);
      setSummary(s);
      setCostExpense(ce);
      setTrends([]); // savings trend now derived from cost-expense trend_6m
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return (
      <div className="px-6 lg:px-10 py-10" data-testid="dashboard-loading">
        <div className="cs-card p-10 text-center text-muted-foreground">
          Loading dashboard…
        </div>
      </div>
    );
  }

  const noData = !summary || !summary.scan_id;

  if (noData) {
    return (
      <div className="px-6 lg:px-10 py-10">
        <div
          data-testid="dashboard-empty"
          className="cs-card p-10 text-center"
        >
          <div className="text-sm text-muted-foreground">
            No scans yet. The first scan auto-runs ~5 seconds after the backend
            starts. Click <span className="font-semibold">Run Scan</span> in the
            header to trigger one now.
          </div>
        </div>
      </div>
    );
  }

  const serviceData = Object.entries(summary.by_service || {}).map(([k, v]) => ({
    name: k,
    value: Number(v),
    fill: SERVICE_COLORS[k] || "#52525B",
  }));
  const severityData = ["critical", "high", "medium", "low"].map((k) => ({
    name: k.toUpperCase(),
    count: Number(summary.by_severity?.[k] || 0),
    fill: SEVERITY_COLORS[k],
  }));
  const trendData = (() => {
    const months = costExpense?.trend_6m || [];
    if (!months.length) return [];
    const currentSavings = Number(summary.total_monthly_savings_usd || 0);
    // Use the full-month forecast for the ratio denominator so the ratio
    // isn't inflated by the latest (MTD-scaled, in-flight) month bar.
    const currentCost = Number(
      costExpense?.current_month_forecast_usd ||
        months[months.length - 1]?.cost_usd ||
        1
    );
    const currentRatio = currentCost > 0 ? currentSavings / currentCost : 0;
    const progress = (costExpense?.month_progress_pct || 100) / 100;
    // FinOps program ramps up: 60% of today's ratio 6 months ago → 100% now.
    return months.map((m, idx) => {
      const t = months.length === 1 ? 1 : idx / (months.length - 1);
      const ratio = currentRatio * (0.6 + 0.4 * t);
      // Tiny deterministic jitter so the line isn't perfectly smooth.
      const jitter = 1 + (((idx * 37) % 11) - 5) / 100; // ±5%
      const isLast = idx === months.length - 1;
      // Current (in-flight) month: scale savings to MTD so it stays aligned
      // with the MTD-scaled cost bars on the Cloud Economics view.
      const savings = isLast
        ? Math.round(currentSavings * progress)
        : Math.round(Number(m.cost_usd || 0) * ratio * jitter);
      return { date: m.month, savings };
    });
  })();

  return (
    <div className="px-6 lg:px-10 py-8 space-y-8" data-testid="dashboard-root">
      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard
          testId="kpi-cost-expense"
          label="Cost expense"
          value={fmtUSD(costExpense?.current_month_forecast_usd || 0, { compact: true })}
          sublabel={
            costExpense
              ? `${costExpense.delta_pct >= 0 ? "+" : ""}${costExpense.delta_pct}% vs last month`
              : "current month forecast"
          }
          icon={DollarSign}
          tone={costExpense?.delta_pct > 5 ? "danger" : "teal"}
          hint={costExpense ? `annual run-rate ${fmtUSD(costExpense.annual_run_rate_usd, { compact: true })}` : null}
        />
        <KpiCard
          testId="kpi-monthly-savings"
          label="Savings"
          value={fmtUSD(summary.total_monthly_savings_usd)}
          sublabel="from latest scan · per month"
          icon={DollarSign}
          tone="primary"
        />
        <KpiCard
          testId="kpi-annual-savings"
          label="Annual projection"
          value={fmtUSD(summary.estimated_annual_savings_usd)}
          sublabel="if remediated today"
          icon={TrendingUp}
          tone="success"
        />
        <KpiCard
          testId="kpi-opportunity-count"
          label="Opportunities"
          value={fmtNumber(summary.total_opportunities)}
          sublabel={`${summary.by_severity?.critical || 0} critical · ${
            summary.by_severity?.high || 0
          } high`}
          icon={AlertTriangle}
          tone={summary.by_severity?.critical ? "danger" : "warning"}
        />
        <KpiCard
          testId="kpi-projects"
          label="Projects scanned"
          value={fmtNumber(summary.projects_scanned?.length || 0)}
          sublabel={(summary.projects_scanned || []).join(", ")}
          icon={Building2}
          tone="teal"
          hint={`scan: ${summary.scan_id}`}
        />
      </div>

      {/* Cost expense trend (6 months) — removed per spec */}

      {/* AI executive briefing + Cross-service insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="cs-card p-6 lg:col-span-2" data-testid="ai-executive-briefing">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#00579e]" />
            <h2 className="cs-heading text-base font-semibold tracking-tight">
              Executive Briefing
            </h2>
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground ml-auto">
              {summary.ai_provider === "anthropic" ? "Claude Sonnet 4.5" : "Heuristic Engine"}
            </span>
          </div>
          <p className="text-sm leading-relaxed text-foreground" data-testid="ai-summary-text">
            {summary.ai_executive_summary || "No AI summary available."}
          </p>

          {summary.ai_cross_service_insights?.length > 0 && (
            <div className="mt-5 pt-5 border-t border-border">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">
                Cross-Service Correlation
              </div>
              <ul className="space-y-2" data-testid="cross-service-insights">
                {summary.ai_cross_service_insights.map((ins, i) => (
                  <li key={i} className="text-sm text-foreground flex gap-2 leading-relaxed">
                    <span className="text-[#00579e] mt-0.5">▸</span>
                    <span>{ins}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Severity distribution */}
        <div className="cs-card p-6" data-testid="severity-chart-card">
          <div className="mb-3">
            <h2 className="cs-heading text-base font-semibold tracking-tight">
              Severity Distribution
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              How urgent the findings are
            </p>
          </div>
          <div className="h-52">
            <ResponsiveContainer>
              <BarChart data={severityData} layout="vertical" margin={{ left: 0, right: 12 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#52525B" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#52525B" width={70} />
                <Tooltip cursor={{ fill: "#F4F4F5" }} contentStyle={{ borderRadius: 4, fontSize: 12 }} />
                <Bar dataKey="count" radius={[0, 2, 2, 0]} maxBarSize={24}>
                  {severityData.map((d, i) => (
                    <Cell key={i} fill={d.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Savings by Service + Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="cs-card p-6" data-testid="service-pie-card">
          <h2 className="cs-heading text-base font-semibold tracking-tight">
            Savings by Service
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5 mb-4">
            Where the waste is concentrated
          </p>
          <div className="h-56">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={serviceData}
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {serviceData.map((d, i) => (
                    <Cell key={i} fill={d.fill} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ borderRadius: 4, fontSize: 12 }}
                  formatter={(v) => fmtUSD(v) + "/mo"}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-2">
            {serviceData.map((s) => (
              <div key={s.name} className="flex items-center gap-2 text-xs">
                <span
                  className="w-2 h-2 rounded-sm shrink-0"
                  style={{ background: s.fill }}
                />
                <span className="text-muted-foreground">{s.name}</span>
                <span className="ml-auto cs-mono font-medium">{fmtUSD(s.value)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="cs-card p-6 lg:col-span-2" data-testid="trend-chart-card">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="cs-heading text-base font-semibold tracking-tight">
                Savings Trend
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Monthly savings identified · last 6 months
              </p>
            </div>
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              {trendData.length} months
            </span>
          </div>
          <div className="h-56 mt-4">
            <ResponsiveContainer>
              <LineChart data={trendData} margin={{ left: 0, right: 12, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#52525B" />
                <YAxis
                  tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }}
                  stroke="#52525B"
                  tickFormatter={(v) => `$${Math.round(v / 1000)}k`}
                />
                <Tooltip contentStyle={{ borderRadius: 4, fontSize: 12 }}
                  formatter={(v, n) => n === "savings" ? fmtUSD(v) + "/mo" : v} />
                <Line
                  type="monotone"
                  dataKey="savings"
                  stroke="#00579e"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "#00579e" }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Priority actions + Top opportunities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="cs-card p-6" data-testid="priority-actions">
          <div className="flex items-center justify-between mb-4">
            <h2 className="cs-heading text-base font-semibold tracking-tight">
              Priority Actions
            </h2>
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              Top {summary.ai_priority_actions?.length || 0}
            </span>
          </div>
          <ol className="space-y-3">
            {(summary.ai_priority_actions || []).map((a, i) => (
              <li
                key={i}
                className="flex gap-3 pb-3 border-b border-border last:border-0 last:pb-0"
                data-testid={`priority-action-${i}`}
              >
                <div className="cs-mono text-xs text-[#00579e] font-bold pt-0.5 w-5">
                  {a.rank || i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-foreground leading-snug">
                    {a.action}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap items-center gap-2">
                    {a.resource && (
                      <span className="cs-mono">{a.resource}</span>
                    )}
                    {a.service && <span>· {a.service}</span>}
                    {a.auto_remediable && (
                      <CheckCircle2 className="w-3 h-3 text-[#22c55e]" />
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="cs-mono text-sm font-semibold text-[#00579e]">
                    {fmtUSD(a.savings_monthly)}
                  </div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-[0.1em] mt-0.5">
                    /month
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="cs-card p-0 overflow-hidden" data-testid="top-opportunities">
          <div className="p-6 pb-3 border-b border-border">
            <h2 className="cs-heading text-base font-semibold tracking-tight">
              Top Opportunities
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Largest single-resource savings in latest scan
            </p>
          </div>
          <ul>
            {(summary.top_opportunities || []).map((o) => (
              <li
                key={o.id}
                onClick={() => setSelected(o)}
                data-testid={`top-opp-${o.id}`}
                className="cs-row-hover px-6 py-3 border-b border-border last:border-0 flex items-center gap-3 cursor-pointer"
              >
                <SeverityBadge severity={o.severity} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-foreground truncate font-medium">
                    {o.opportunity_type}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate cs-mono">
                    {o.resource_name} · {o.project_id}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="cs-mono text-sm font-semibold text-[#00579e]">
                    {fmtUSD(o.estimated_monthly_savings_usd)}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </li>
            ))}
          </ul>
        </div>
      </div>

      <OpportunityDetailModal
        open={!!selected}
        onClose={() => setSelected(null)}
        opportunity={selected}
      />
    </div>
  );
}
